import type { CSSProperties } from 'react';
import type { ReservationThemeTokens } from './reservationTheme';

// Only opaque hex colors are accepted: an unknown/translucent backdrop cannot
// provide a reliable contrast guarantee. Persisted settings are never mutated.
export const opaqueColor = (value: string, fallback = '#ffffff'): string => {
  if (/^#[\da-f]{6}$/i.test(value)) return value;
  if (/^#[\da-f]{3}$/i.test(value)) return '#' + [...value.slice(1)].map(c => c + c).join('');
  return fallback;
};

export const contrastRatio = (a: string, b: string): number => {
  const luminance = (color: string) => {
    const rgb = opaqueColor(color).slice(1).match(/../g)!.map(c => {
      const channel = parseInt(c, 16) / 255;
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
  };
  const values = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (values[0] + 0.05) / (values[1] + 0.05);
};

export const readableColor = (background: string, preferred: string): string => {
  if (/^#([\da-f]{3}|[\da-f]{6})$/i.test(preferred) && contrastRatio(background, preferred) >= 4.5) return preferred;
  return contrastRatio(background, '#000000') > contrastRatio(background, '#ffffff') ? '#000000' : '#ffffff';
};

type SurfaceStyle = CSSProperties & Record<`--${string}`, string>;
export const manageSurfaceStyle = (background: string, colors: Pick<ReservationThemeTokens['colors'], 'textPrimary' | 'textSecondary' | 'primary' | 'accent' | 'danger'>): SurfaceStyle => {
  const opaque = opaqueColor(background);
  const primary = readableColor(opaque, colors.textPrimary);
  const secondary = readableColor(opaque, colors.textSecondary);
  return {
    backgroundColor: opaque,
    color: primary,
    '--manage-text-primary': primary,
    '--manage-text-secondary': secondary,
    '--color-text-primary': primary,
    '--color-text-secondary': secondary,
    '--manage-primary': readableColor(opaque, colors.primary),
    '--manage-accent': readableColor(opaque, colors.accent),
    '--manage-danger': readableColor(opaque, colors.danger),
  };
};

export const MANAGE_GLASS_ALPHA = 0.74;

// A blur removes detail, not luminance. Bound source-over compositing against
// both black and white so text stays readable over any SDR photo, without
// fetching/sampling images (which can fail for cross-origin backgrounds).
export const compositeGlassColor = (tint: string, backdrop: string): string => {
  const channels = (color: string) => opaqueColor(color).slice(1).match(/../g)!.map(c => parseInt(c, 16));
  const base = channels(backdrop);
  return '#' + channels(tint).map((channel, i) =>
    Math.round(channel * MANAGE_GLASS_ALPHA + base[i] * (1 - MANAGE_GLASS_ALPHA)).toString(16).padStart(2, '0')
  ).join('');
};

export const manageGlassStyle = (
  layer: 'outer' | 'panel' | 'surface',
  colors: Pick<ReservationThemeTokens['colors'], 'textPrimary' | 'textSecondary' | 'primary' | 'accent' | 'danger'>,
): SurfaceStyle => {
  const tint = layer === 'outer' ? '#282828' : layer === 'panel' ? '#181818' : '#202020';
  const darkest = compositeGlassColor(tint, '#000000');
  const lightest = compositeGlassColor(tint, '#ffffff');
  const readable = (preferred: string, fallback = '#ffffff') => {
    // Reject a foreground inside the possible background luminance interval,
    // even if endpoint contrast alone could otherwise look acceptable.
    const luminance = (color: string) => contrastRatio(color, '#000000');
    const valid = /^#([\da-f]{3}|[\da-f]{6})$/i.test(preferred);
    const outside = valid && (luminance(preferred) < luminance(darkest) || luminance(preferred) > luminance(lightest));
    return outside && Math.min(contrastRatio(preferred, darkest), contrastRatio(preferred, lightest)) >= 4.5
      ? preferred : Math.min(contrastRatio(fallback, darkest), contrastRatio(fallback, lightest)) >= 4.5 ? fallback : '#ffffff';
  };
  const primary = readable(colors.textPrimary);
  const secondary = readable(colors.textSecondary, '#e5e5e5');
  const channel = parseInt(tint.slice(1, 3), 16);
  return {
    backgroundColor: `rgba(${channel}, ${channel}, ${channel}, ${MANAGE_GLASS_ALPHA})`,
    backdropFilter: 'blur(22px) saturate(0.9)',
    WebkitBackdropFilter: 'blur(22px) saturate(0.9)',
    color: primary,
    borderColor: 'rgba(255, 255, 255, 0.14)',
    boxShadow: layer === 'outer'
      ? '0 18px 45px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.10)'
      : 'inset 0 2px 8px rgba(0,0,0,0.30), inset 0 -1px 0 rgba(255,255,255,0.04)',
    colorScheme: 'dark',
    '--manage-text-primary': primary,
    '--manage-text-secondary': secondary,
    '--color-text-primary': primary,
    '--color-text-secondary': secondary,
    '--manage-primary': readable(colors.primary, '#a7f3d0'),
    '--manage-accent': readable(colors.accent, '#bae6fd'),
    '--manage-danger': readable(colors.danger, '#fecaca'),
  };
};
